import { ButtonGroup, TextButton } from '@oneaudi/unified-web-alpha-components';
import { useCallback, useMemo } from 'react';
import {
	SummarySection,
	SummarySectionCa,
	TopSection,
} from '~/app/compositions/content/configuration-overview-builder';
import {
	ConfigurationImagesStyle,
	ConfigurationImageWrapperStyle,
	ConfigurationOverviewStyle,
	ConfigurationSectionWrapperStyle,
	ConfigurationSummaryStyle,
} from '~/app/compositions/content/configuration-overview-style';
import { EecLabelSection } from '~/app/compositions/content/eec/eec-label-section';
import { useGalleryAltText } from '~/app/compositions/content/gallery/hooks/use-gallery-alt-text';
import { GPSRSection } from '~/app/compositions/content/gpsr';
import { SummaryCtaButton } from '~/app/compositions/content/service-footer/components/user-marketing/main-cta-button';
import { useDialogs } from '~/app/hooks/use-dialogs';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { useMarketUiConfig } from '~/app/market-config/use-market-ui-config';
import { AutoFlowGrid } from '~/app/pages/components/auto-flow-grid';
import { StockCarEnabled, useStockCarDialog } from '~/app/pages/configurator/components/dialogs/stock-car-dialog';
import { ImageViewsEnum } from '~/app/pages/configurator/models/image-views';
import { useSelectedEquipment } from '~/app/pages/summary/hooks/use-selected-equipment';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { technicalDataPresent } from '~/app/utils/technical-data-present';
import { useCatalog, useConfigurator } from '~/graphql/hooks/use-configurator';
import { useConfiguredCarTechnicalDataQuery } from '~/graphql/hooks/use-configured-car-queries';
import { useImages } from '~/graphql/hooks/use-images';
import { checkEventActionUrl } from '~/graphql/utils/check-event-action-url';
import GetUrlFromImageSet from '~/graphql/utils/get-from-image-set';
import { modifyImageResolution } from '~/graphql/utils/modify-image-url';
import { DATA_TEST_ID } from '~/test/data-test-id';
import { getEecLabelUrl, getEnvkvLabelUrls } from '~/utils/technical-data-media-items';
import { useAppState } from '~/app/contexts/app-context';
import { PriceSection } from './components/price-section';
import { useVATConfig } from '~/app/market-config/use-vat-config';
import { useAtomValue } from 'jotai';
import { useCurrencyFormatter } from '~/hooks/use-currency-formatter';
import { state } from '~/storage';
import { useAtom } from 'jotai/index';

export function ConfigurationOverviewCa() {
	const { openDialog } = useDialogs();
	const { priceSummary } = useCatalog();
	const { isLoading, carIdentifier } = useConfigurator();
	const vatConfig = useVATConfig();
	const disclaimerText = useAtomValue(state.financingDisclaimerText);
	const { formatCurrency } = useCurrencyFormatter();
	const { config } = useMarketConfig();
	const { consumptionAndEmissionSummary } = useMarketUiConfig();
	const { showStockCarDialog } = useStockCarDialog(config.stockCarConfig.geoFilter);
	const { i18n } = useI18n();
	const [financialError] = useAtom(state.financialError);

	let priceRemark: string | null = null;
	if (config.useStaticPriceDateRemark) {
		priceRemark = config.useSummaryAlternativePriceDateRemark
			? i18n('ngc.summary.alternativeRemark')
			: i18n('ngc.summary.remark');
	} else if (priceSummary?.priceDate?.text) {
		priceRemark = priceSummary?.priceDate?.text;
	}

	const {
		configuration: { featuresAndPackages },
	} = useSelectedEquipment();

	const { imagesByProfileId, mediaByProfileId } = useImages();

	const [color, seats] = ['Exterior_Colors', ':Interior_Tiles'].map((id) =>
		featuresAndPackages.find((feature) => feature.id.includes(id)),
	);

	const { loading, data: technicalData } = useConfiguredCarTechnicalDataQuery(carIdentifier);

	const labelUrl = useMemo(() => {
		const url = getEnvkvLabelUrls(technicalData?.configuredCar?.technicalDataV2?.mediaItems).svgUrl;
		return url || undefined;
	}, [technicalData?.configuredCar?.technicalDataV2?.mediaItems, technicalData]);

	const pdfUrl = useMemo(() => {
		const url = getEnvkvLabelUrls(technicalData?.configuredCar?.technicalDataV2?.mediaItems).pdfUrl;
		return url || undefined;
	}, [technicalData?.configuredCar?.technicalDataV2?.mediaItems, technicalData]);

	const eecUrl = useMemo(() => {
		const url = getEecLabelUrl(technicalData?.configuredCar?.technicalDataV2?.mediaItems);
		return url || undefined;
	}, [technicalData?.configuredCar?.technicalDataV2?.mediaItems, technicalData]);

	const { financingData } = useAppState();

	const impressionContainerRef = useCallback(
		(node: HTMLElement) => {
			if (node !== null && !isLoading) {
				appTrackingService.summaryImpression({
					node,
					eventName: 'configurator (config overview) - impression',
					componentName: 'configuration overview',
				});
			}
		},
		[isLoading],
	);

	const impressionCTARef = useCallback(
		(node: HTMLDivElement) => {
			if (node !== null && !isLoading) {
				const buttonNodes = Array.from(node.childNodes[0].childNodes || []) as HTMLElement[];
				appTrackingService.summaryImpression({
					node,
					eventName: 'configurator (config overview buttons) - impression',
					componentName: 'configuration overview',
					relatedElements: buttonNodes.map((buttonNode, index) => ({
						elementName: 'cta',
						elementValue: `${index + 1}-${buttonNode.innerText}`,
					})),
				});
			}
		},
		[isLoading],
	);

	const trackClickEvent = useCallback((eventName: string, label: string, url?: string) => {
		const buttonsContainerRef = document.querySelector('.buttons-group-cta');
		const buttonNodes = buttonsContainerRef?.getElementsByTagName('button');
		const nodeIndex = Array.from(buttonNodes || [])?.findIndex((buttonNode) => buttonNode.innerText === label);

		if (buttonNodes && nodeIndex !== -1) {
			appTrackingService.summaryClickEvent({
				eventAction: checkEventActionUrl(url || ''),
				eventName: `configurator (configuration overview) - click on ${eventName}`,
				componentName: 'configurator-configuration-overview',
				label,
				targetURL: url,
				elementName: 'button',
				relatedElements: [
					{
						elementName: 'cta',
						elementValue: `${nodeIndex + 1}-${buttonNodes[nodeIndex].id}`,
					},
				],
			});
		}
	}, []);

	return (
		<ConfigurationOverviewStyle
			ref={impressionContainerRef}
			data-testid={DATA_TEST_ID.SUMMARY_CONFIGURATION_OVERVIEW_CONTAINER}>
			<TopSection techDataPresent={technicalDataPresent(technicalData?.configuredCar?.technicalDataV2)} />
			<ConfigurationSummaryStyle>
				<ConfigurationSectionWrapperStyle>
					{config.enableFinancingServices &&
					financingData?.financingDetails &&
					financingData.financing &&
					!financialError ? (
						<SummarySectionCa financing={financingData} />
					) : (
						<>
							<SummarySection />
							{config.showPrices && (
								<PriceSection
									totalPrice={formatCurrency(priceSummary?.totalPrice, vatConfig.totalPrice) ?? ''}
									monthlyRate={null}
									remark={priceRemark}
									disclaimer={disclaimerText}
									showSpecialOffers={config.specialOfferUrl.length > 0}
								/>
							)}
						</>
					)}

					<div ref={impressionCTARef}>
						<ButtonGroup className="buttons-group-cta" orientation="vertical">
							{config.showPrices && (
								<TextButton
									id="price details"
									variant="secondary"
									size={'md'}
									isFullWidth
									onClick={() => {
										openDialog('priceDetails');
										trackClickEvent(
											'open price details layer',
											i18n('ngc.actions.showPriceDetails'),
										);
									}}
									aria-label={i18n('ngc.actions.showPriceDetails')}
									data-testid={DATA_TEST_ID.SUMMARY_PRICE_DETAIL_BUTTON}>
									{i18n('ngc.actions.showPriceDetails')}
								</TextButton>
							)}
							<SummaryCtaButton
								trackClickEvent={trackClickEvent}
								variant="secondary"
								size="md"
								isSecondCta={'false'}
								isFullWidth
							/>
							{StockCarEnabled() && (
								<TextButton
									id="Car Matching"
									className="main-cta-button"
									isFullWidth
									variant="secondary"
									onClick={() => {
										showStockCarDialog();
										trackClickEvent('stock car matching', i18n('ngc.stockcar.trigger'));
									}}
									size={'md'}
									aria-label={i18n('ngc.stockcar.trigger')}
									data-testid={DATA_TEST_ID.STOCK_CAR_BUTTON}>
									{i18n('ngc.stockcar.trigger')}
								</TextButton>
							)}
						</ButtonGroup>
					</div>
				</ConfigurationSectionWrapperStyle>
				<ConfigurationImageWrapperStyle>
					<ConfigurationImagesStyle>
						<AutoFlowGrid
							direction="bigImageFirst"
							sources={[
								{
									src: modifyImageResolution(
										imagesByProfileId[ImageViewsEnum.EXTERIOR_REAR_3_4],
										'960',
									),
									alt: useGalleryAltText(mediaByProfileId[ImageViewsEnum.EXTERIOR_REAR_3_4]),
								},
								{
									src:
										GetUrlFromImageSet(
											color?.families?.[0]?.items?.[0]?.imageSets,
											'outer_tile_button',
										) ||
										GetUrlFromImageSet(
											color?.families?.[0]?.items?.[0]?.imageSets,
											'outer_tile_hd_square',
										) ||
										'//about:blank',
									alt: color?.families?.[0]?.items?.[0].name || i18n('ngc.a11y.tiles.notavailable'),
								},
								{
									src: modifyImageResolution(
										imagesByProfileId[ImageViewsEnum.EXTERIOR_WHEELS_RIM],
										'480',
									),
									alt: useGalleryAltText(mediaByProfileId[ImageViewsEnum.EXTERIOR_WHEELS_RIM]),
								},
							]}
						/>
						<AutoFlowGrid
							direction="smallImagesFirst"
							sources={[
								{
									src: modifyImageResolution(
										imagesByProfileId[ImageViewsEnum.LIGHTS_FRONT_HEADLIGHTS],
										'480',
									),
									alt: useGalleryAltText(mediaByProfileId[ImageViewsEnum.LIGHTS_FRONT_HEADLIGHTS]),
								},
								{
									src:
										GetUrlFromImageSet(
											seats?.families?.[0]?.items?.[0]?.imageSets,
											'interior_tile_button',
										) || '//about:blank',
									alt: seats?.families?.[0]?.items?.[0].name || i18n('ngc.a11y.tiles.notavailable'),
								},
								{
									src: modifyImageResolution(
										imagesByProfileId[ImageViewsEnum.INTERIOR_DASHBOARD_DASHBOARD],
										'960',
									),
									alt: useGalleryAltText(
										mediaByProfileId[ImageViewsEnum.INTERIOR_DASHBOARD_DASHBOARD],
									),
								},
							]}
						/>
						{consumptionAndEmissionSummary}
					</ConfigurationImagesStyle>
				</ConfigurationImageWrapperStyle>
			</ConfigurationSummaryStyle>
			{!loading && config.showEECLabel && config.wltpInfo && (
				<EecLabelSection url={labelUrl} pdfURL={pdfUrl} eecURL={eecUrl} />
			)}
			<GPSRSection />
		</ConfigurationOverviewStyle>
	);
}
